import React from 'react';

export type AcConfiguratorSectionId =
  | 'coordinator'
  | 'syncWorkers'
  | 'asyncWorkers'
  | 'metaPrompts';

export interface AcCollapsibleSectionProps {
  id?: AcConfiguratorSectionId;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  toolbar?: React.ReactNode;
  /** Keep toolbar visible when the section is collapsed (e.g. worker Duplicate / Delete). */
  showToolbarWhenCollapsed?: boolean;
  /** 'below' stacks toolbar under the title row (better for narrow columns). */
  toolbarPlacement?: 'inline' | 'below';
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  titleVariant?: 'badge' | 'plain';
}

const CollapseIcon: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <span className={`ac-collapsible__icon${collapsed ? ' ac-collapsible__icon--collapsed' : ''}`} aria-hidden>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.5 5.25L7 8.75L10.5 5.25"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const AcCollapsibleSection: React.FC<AcCollapsibleSectionProps> = ({
  id,
  title,
  subtitle,
  collapsed,
  onToggle,
  toolbar,
  showToolbarWhenCollapsed = false,
  toolbarPlacement = 'inline',
  children,
  className = 'ac-card',
  style,
  titleVariant = 'badge',
}) => {
  const hasBody = Boolean(children);
  const toolbarBelow = toolbarPlacement === 'below';
  const showToolbar = Boolean(toolbar) && (!collapsed || showToolbarWhenCollapsed);

  return (
    <section
      className={`ac-collapsible ${className}${collapsed ? ' ac-collapsible--collapsed' : ''}${toolbarBelow ? ' ac-collapsible--toolbar-below' : ''}`}
      style={style}
      data-ac-section={id}
    >
      <div className="ac-collapsible__header">
        <button
          type="button"
          className="ac-collapsible__toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <CollapseIcon collapsed={collapsed} />
          <div className="ac-collapsible__titles">
            {titleVariant === 'plain' ? (
              <span className="ac-collapsible__title-plain">{title}</span>
            ) : (
              <span className="ac-badge">{title}</span>
            )}
            {subtitle != null && subtitle !== '' ? (
              <span className="ac-subtitle">{subtitle}</span>
            ) : null}
          </div>
        </button>
        {showToolbar && !toolbarBelow ? <div className="ac-collapsible__toolbar">{toolbar}</div> : null}
      </div>
      {showToolbar && toolbarBelow ? (
        <div className="ac-collapsible__toolbar-below">{toolbar}</div>
      ) : null}
      {!collapsed && hasBody ? <div className="ac-collapsible__body">{children}</div> : null}
    </section>
  );
};

export default AcCollapsibleSection;
